import dayjs from 'dayjs';
import { memoryDateLabel } from 'helpers/memory-date-label';
import { byAlbumOrder } from 'helpers/memory-order';
import { AlbumContentSerialized } from 'networking/types/album-detail';

// newest/oldest go by the date set on a memory; recentlyAdded/earliestAdded
// by when it was added.
export type AlbumSortKey =
  | 'newest'
  | 'oldest'
  | 'recentlyAdded'
  | 'earliestAdded'
  | 'aToZ'
  | 'zToA'
  | 'custom';

export type AlbumViewKind = 'grid' | 'planner';

export const ALBUM_SORT_OPTIONS: { value: AlbumSortKey; label: string }[] = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'recentlyAdded', label: 'Recently added' },
  { value: 'earliestAdded', label: 'Earliest added' },
  { value: 'aToZ', label: 'A-Z' },
  { value: 'zToA', label: 'Z-A' },
];

// Offered only once the album has been arranged by hand.
export const CUSTOM_SORT_OPTION: { value: AlbumSortKey; label: string } = {
  value: 'custom',
  label: 'Custom order',
};

// "Custom order" is offered once the album has been arranged by hand.
export const albumSortOptions = (showCustomOrder: boolean) =>
  showCustomOrder
    ? [CUSTOM_SORT_OPTION, ...ALBUM_SORT_OPTIONS]
    : ALBUM_SORT_OPTIONS;

const contentDate = (content: AlbumContentSerialized) =>
  content.startDate || content.fileDate || content.createdAt;

const addedTime = (content: AlbumContentSerialized) =>
  dayjs(content.createdAt).valueOf() || 0;

// The memory's own date as it was entered (a range, or just a year or month);
// else the day its file was made or it was added.
export const formatContentDate = (content: AlbumContentSerialized) => {
  const own = memoryDateLabel(content.startDate, content.endDate);
  if (own) return own;
  const date = contentDate(content);
  return date ? dayjs(date).format('MMMM D, YYYY') : '';
};

export const sortAlbumContents = (
  contents: AlbumContentSerialized[],
  sortBy: AlbumSortKey,
) => {
  const sorted = [...contents];
  switch (sortBy) {
    // The album's own order (helpers/memory-order).
    case 'custom':
      return byAlbumOrder(sorted);
    case 'aToZ':
      return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    case 'zToA':
      return sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
    case 'newest':
      return sorted.sort(
        (a, b) => dayjs(contentDate(b)).unix() - dayjs(contentDate(a)).unix(),
      );
    case 'recentlyAdded':
      return sorted.sort((a, b) => addedTime(b) - addedTime(a));
    case 'earliestAdded':
      return sorted.sort((a, b) => addedTime(a) - addedTime(b));
    case 'oldest':
    default:
      return sorted.sort(
        (a, b) => dayjs(contentDate(a)).unix() - dayjs(contentDate(b)).unix(),
      );
  }
};

// A recordable entry (video/audio) that has no recording attached yet acts as
// a prompt: it shows up in the Planner "Prompts" column until it is answered.
export { isPromptContent } from 'helpers/memories';
